import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { buildWebsiteUrl, useOrgSlugInfo } from '@/hooks/useOrgSlug';
import { invalidateAdminInboxCounts } from '@/hooks/useAdminInboxCounts';
import type { CurrencyCode } from '@/lib/currency';
import {
  cleanFrequency,
  computeQuoteTotals,
  normaliseVatRate,
  quotePath,
  type QuoteStatus,
} from '@/lib/quotes';

/**
 * Priced quotes — org-scoped CRUD over public.quotes / public.quote_lines.
 *
 * Like useInvoices, the generated Supabase types predate these tables, so rows
 * are typed locally and the client is cast. RLS ("Org members can manage
 * quotes") governs access; the `.eq(organization_id)` filters keep queries on
 * the active org. Quote numbers are assigned by a DB trigger.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface QuoteListRow {
  id: string;
  quote_number: string;
  status: QuoteStatus;
  client_name: string;
  client_email: string | null;
  total_amount: number;
  currency_code: CurrencyCode;
  valid_until: string | null;
  created_at: string;
  sent_at: string | null;
  accepted_at: string | null;
  enquiry_id: string | null;
  job_id: string | null;
  invoice_id: string | null;
  public_token: string;
}

export interface QuoteLine {
  id?: string;
  description: string;
  service_id: string | null;
  quantity: number;
  unit_amount: number;
  line_total?: number;
  is_optional: boolean;
  is_selected?: boolean;
}

export interface QuoteDetail extends QuoteListRow {
  organization_id: string;
  customer_id: string | null;
  client_phone: string | null;
  client_address: string | null;
  frequency: string | null;
  notes: string | null;
  vat_rate: number;
  subtotal_amount: number;
  vat_amount: number;
  viewed_at: string | null;
  accepted_name: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  lines: QuoteLine[];
}

export interface SaveQuoteInput {
  id?: string | null;
  organization_id: string;
  enquiry_id: string | null;
  customer_id: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  client_address: string | null;
  frequency: string | null;
  notes: string | null;
  valid_until: string | null;
  currency_code: CurrencyCode;
  vat_rate: number;
  lines: QuoteLine[];
  /** Optional status change saved with the edit (e.g. re-opening an expired quote). */
  status?: QuoteStatus;
}

export interface QuoteVatSettings {
  companyInfoId: string | null;
  vat_registered: boolean;
  vat_rate: number;
  vat_number: string;
  currency_code: CurrencyCode;
}

export interface QuotePriceListItem {
  id: string;
  name: string;
  price: number | null;
  is_quote_only: boolean;
}

export interface QuotePrefill {
  enquiry_id: string | null;
  customer_id: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  client_address: string | null;
  frequency: string | null;
  service_id: string | null;
  notes: string | null;
}

const LIST_COLUMNS =
  'id, quote_number, status, client_name, client_email, total_amount, currency_code, valid_until, created_at, sent_at, accepted_at, enquiry_id, job_id, invoice_id, public_token';

const toNum = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const quotesKey = (orgId: string | null | undefined) => ['quotes', orgId] as const;
export const quoteKey = (id: string | null | undefined) => ['quote', id] as const;

/** All quotes for the org, newest first. */
export function useQuotes(orgId: string | null | undefined) {
  return useQuery({
    queryKey: quotesKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<QuoteListRow[]> => {
      const { data, error } = await db
        .from('quotes')
        .select(LIST_COLUMNS)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return ((data ?? []) as QuoteListRow[]).map((row) => ({ ...row, total_amount: toNum(row.total_amount) }));
    },
  });
}

/** One quote with its lines (for the editor). */
export function useQuoteDetail(quoteId: string | null | undefined) {
  return useQuery({
    queryKey: quoteKey(quoteId),
    enabled: Boolean(quoteId),
    queryFn: async (): Promise<QuoteDetail | null> => {
      const { data, error } = await db
        .from('quotes')
        .select('*, quote_lines(*)')
        .eq('id', quoteId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const { quote_lines, ...header } = data;
      const lines = ((quote_lines ?? []) as Array<QuoteLine & { sort_order?: number }>)
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((line) => ({
          id: line.id,
          description: line.description,
          service_id: line.service_id ?? null,
          quantity: toNum(line.quantity),
          unit_amount: toNum(line.unit_amount),
          line_total: toNum(line.line_total),
          is_optional: Boolean(line.is_optional),
          is_selected: Boolean(line.is_selected),
        }));
      return {
        ...header,
        vat_rate: toNum(header.vat_rate),
        subtotal_amount: toNum(header.subtotal_amount),
        vat_amount: toNum(header.vat_amount),
        total_amount: toNum(header.total_amount),
        lines,
      } as QuoteDetail;
    },
  });
}

/** Active services — the reusable price list for quote lines. */
export function useQuotePriceList(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['quote-price-list', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<QuotePriceListItem[]> => {
      const { data, error } = await db
        .from('services')
        .select('id, name, price, is_quote_only')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Array<{ id: string; name: string | null; price: unknown; is_quote_only: boolean | null }>).map(
        (row) => ({
          id: row.id,
          name: row.name ?? 'Service',
          price: row.price == null || !Number.isFinite(Number(row.price)) ? null : Number(row.price),
          is_quote_only: Boolean(row.is_quote_only),
        }),
      );
    },
  });
}

/** Org VAT settings (company_info) used to default new quotes. */
export function useQuoteVatSettings(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['quote-vat-settings', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<QuoteVatSettings> => {
      const { data, error } = await db
        .from('company_info')
        .select('id, vat_registered, vat_rate, vat_number, currency_code')
        .eq('organization_id', orgId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return {
        companyInfoId: data?.id ?? null,
        vat_registered: Boolean(data?.vat_registered),
        vat_rate: data?.vat_rate == null ? 20 : toNum(data.vat_rate),
        vat_number: data?.vat_number ?? '',
        currency_code: (data?.currency_code ?? 'GBP') as CurrencyCode,
      };
    },
  });
}

export function useSaveQuoteVatSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orgId: string;
      companyInfoId: string;
      vat_registered: boolean;
      vat_rate: number;
      vat_number: string;
    }) => {
      const { error } = await db
        .from('company_info')
        .update({
          vat_registered: input.vat_registered,
          vat_rate: normaliseVatRate(input.vat_rate),
          vat_number: input.vat_number.trim() || null,
        })
        .eq('id', input.companyInfoId)
        .eq('organization_id', input.orgId);
      if (error) throw error;
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: ['quote-vat-settings', input.orgId] });
    },
  });
}

/** Client details + requested service from an enquiry, for "Create quote". */
export function useQuotePrefillFromEnquiry(orgId: string | null | undefined, enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: ['quote-prefill-enquiry', orgId, enquiryId],
    enabled: Boolean(orgId && enquiryId),
    queryFn: async (): Promise<QuotePrefill | null> => {
      const { data, error } = await db
        .from('enquiries')
        .select('id, customer_id, service_id, client_name, client_email, client_phone, address, frequency, premises_size, additional_info')
        .eq('id', enquiryId)
        .eq('organization_id', orgId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const extra = [
        cleanFrequency(data.premises_size) ? `Property: ${data.premises_size}` : null,
        data.additional_info ? `Customer notes: ${data.additional_info}` : null,
      ].filter(Boolean);
      return {
        enquiry_id: data.id,
        customer_id: data.customer_id ?? null,
        client_name: data.client_name ?? '',
        client_email: data.client_email ?? null,
        client_phone: data.client_phone ?? null,
        client_address: data.address ?? null,
        frequency: cleanFrequency(data.frequency),
        service_id: data.service_id ?? null,
        notes: extra.length ? extra.join('\n') : null,
      };
    },
  });
}

async function writeLines(quoteId: string, organizationId: string, lines: QuoteLine[]) {
  const { error: deleteError } = await db.from('quote_lines').delete().eq('quote_id', quoteId);
  if (deleteError) throw deleteError;
  const rows = lines.map((line, index) => ({
    quote_id: quoteId,
    organization_id: organizationId,
    service_id: line.service_id || null,
    description: line.description.trim(),
    quantity: line.quantity,
    unit_amount: line.unit_amount,
    is_optional: line.is_optional,
    is_selected: false,
    sort_order: index,
  }));
  if (rows.length) {
    const { error } = await db.from('quote_lines').insert(rows);
    if (error) throw error;
  }
}

/**
 * Create or update a quote and replace its lines. Totals are computed here with
 * the shared helpers (optional extras excluded until the customer picks them).
 */
export function useSaveQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveQuoteInput): Promise<{ id: string }> => {
      const vatRate = normaliseVatRate(input.vat_rate);
      const totals = computeQuoteTotals(input.lines, vatRate);
      const header = {
        organization_id: input.organization_id,
        enquiry_id: input.enquiry_id,
        customer_id: input.customer_id,
        client_name: input.client_name.trim(),
        client_email: input.client_email?.trim() || null,
        client_phone: input.client_phone?.trim() || null,
        client_address: input.client_address?.trim() || null,
        frequency: cleanFrequency(input.frequency),
        notes: input.notes?.trim() || null,
        valid_until: input.valid_until || null,
        currency_code: input.currency_code,
        vat_rate: vatRate,
        subtotal_amount: totals.subtotal,
        vat_amount: totals.vat,
        total_amount: totals.total,
        ...(input.status ? { status: input.status } : {}),
      };

      let quoteId = input.id ?? null;
      if (quoteId) {
        const { error } = await db.from('quotes').update(header).eq('id', quoteId).eq('organization_id', input.organization_id);
        if (error) throw error;
      } else {
        const { data, error } = await db.from('quotes').insert(header).select('id').single();
        if (error) throw error;
        quoteId = data.id as string;
      }
      await writeLines(quoteId as string, input.organization_id, input.lines);
      return { id: quoteId as string };
    },
    onSuccess: (result, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.organization_id) });
      queryClient.invalidateQueries({ queryKey: quoteKey(result.id) });
    },
  });
}

/** Copy a quote (lines included) into a fresh draft with a new number + link. */
export function useDuplicateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { quote: QuoteDetail; validUntil: string }): Promise<{ id: string }> => {
      const q = input.quote;
      const { data, error } = await db
        .from('quotes')
        .insert({
          organization_id: q.organization_id,
          enquiry_id: q.enquiry_id,
          customer_id: q.customer_id,
          client_name: q.client_name,
          client_email: q.client_email,
          client_phone: q.client_phone,
          client_address: q.client_address,
          frequency: q.frequency,
          notes: q.notes,
          valid_until: input.validUntil,
          currency_code: q.currency_code,
          vat_rate: q.vat_rate,
          ...(() => {
            const totals = computeQuoteTotals(q.lines.map((l) => ({ ...l, is_selected: false })), q.vat_rate);
            return { subtotal_amount: totals.subtotal, vat_amount: totals.vat, total_amount: totals.total };
          })(),
        })
        .select('id')
        .single();
      if (error) throw error;
      await writeLines(data.id, q.organization_id, q.lines);
      return { id: data.id as string };
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.quote.organization_id) });
    },
  });
}

async function markEnquiryQuoted(orgId: string, enquiryId: string | null) {
  if (!enquiryId) return;
  // Only move forward through the pipeline; never undo converted/declined.
  await db
    .from('enquiries')
    .update({ status: 'quoted' })
    .eq('id', enquiryId)
    .eq('organization_id', orgId)
    .in('status', ['pending', 'contacted']);
}

/**
 * Email the quote link to the client via the `send-quote` edge function, which
 * also marks a draft as sent and moves the enquiry to "quoted".
 */
export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { quoteId: string; organizationId: string }) => {
      const { data, error } = await supabase.functions.invoke('send-quote', {
        body: { quoteId: input.quoteId, organizationId: input.organizationId },
      });
      if (error) throw error;
      const result = (data ?? {}) as { success?: boolean; skipped?: string; recipient?: string; error?: string };
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.organizationId) });
      queryClient.invalidateQueries({ queryKey: quoteKey(input.quoteId) });
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.organizationId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

/**
 * Mark a draft as sent without emailing (e.g. the owner copied the link to send
 * by WhatsApp/text). Draft links don't open for customers until this happens.
 */
export function useMarkQuoteSent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { quoteId: string; organizationId: string; enquiryId: string | null }) => {
      const { error } = await db
        .from('quotes')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', input.quoteId)
        .eq('organization_id', input.organizationId)
        .eq('status', 'draft');
      if (error) throw error;
      await markEnquiryQuoted(input.organizationId, input.enquiryId);
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.organizationId) });
      queryClient.invalidateQueries({ queryKey: quoteKey(input.quoteId) });
      queryClient.invalidateQueries({ queryKey: ['enquiries', input.organizationId] });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

/** Owner-side status change (e.g. "Mark declined" after a phone call). */
export function useSetQuoteStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { quoteId: string; organizationId: string; status: QuoteStatus }) => {
      const patch: Record<string, unknown> = { status: input.status };
      if (input.status === 'declined') patch.declined_at = new Date().toISOString();
      const { error } = await db
        .from('quotes')
        .update(patch)
        .eq('id', input.quoteId)
        .eq('organization_id', input.organizationId);
      if (error) throw error;
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.organizationId) });
      queryClient.invalidateQueries({ queryKey: quoteKey(input.quoteId) });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { quoteId: string; organizationId: string }) => {
      const { error } = await db.from('quotes').delete().eq('id', input.quoteId).eq('organization_id', input.organizationId);
      if (error) throw error;
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(input.organizationId) });
      invalidateAdminInboxCounts(queryClient);
    },
  });
}

/**
 * Turn an accepted quote into a draft invoice (lines that count, VAT as tax),
 * link it back on the quote, and return the new invoice id.
 */
export function useConvertQuoteToInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (quote: QuoteDetail): Promise<{ invoiceId: string }> => {
      if (quote.invoice_id) return { invoiceId: quote.invoice_id };
      const lines = quote.lines.filter((l) => !l.is_optional || l.is_selected);
      const totals = computeQuoteTotals(lines, quote.vat_rate);
      const { data, error } = await db
        .from('invoices')
        .insert({
          organization_id: quote.organization_id,
          customer_id: quote.customer_id,
          customer_name: quote.client_name,
          customer_email: quote.client_email,
          status: 'draft',
          currency_code: quote.currency_code,
          subtotal_amount: totals.subtotal,
          tax_amount: totals.vat,
          total_amount: totals.total,
          notes: `From quote ${quote.quote_number}`,
          booking_id: quote.job_id,
        })
        .select('id')
        .single();
      if (error) throw error;
      const invoiceId = data.id as string;
      if (lines.length) {
        const { error: linesError } = await db.from('invoice_line_items').insert(
          lines.map((line, index) => ({
            invoice_id: invoiceId,
            organization_id: quote.organization_id,
            service_id: line.service_id || null,
            description: line.description,
            quantity: line.quantity,
            unit_amount: line.unit_amount,
            sort_order: index,
          })),
        );
        if (linesError) throw linesError;
      }
      const { error: linkError } = await db
        .from('quotes')
        .update({ invoice_id: invoiceId })
        .eq('id', quote.id)
        .eq('organization_id', quote.organization_id);
      if (linkError) throw linkError;
      return { invoiceId };
    },
    onSuccess: (_r, quote) => {
      queryClient.invalidateQueries({ queryKey: quotesKey(quote.organization_id) });
      queryClient.invalidateQueries({ queryKey: quoteKey(quote.id) });
      queryClient.invalidateQueries({ queryKey: ['invoices', quote.organization_id] });
    },
  });
}

/** Called by the Add Job dialog after booking a job from a quote. */
export async function linkQuoteToJob(orgId: string, quoteId: string, jobId: string) {
  const { error } = await db
    .from('quotes')
    .update({ job_id: jobId })
    .eq('id', quoteId)
    .eq('organization_id', orgId);
  if (error) throw error;
}

/**
 * Builds the customer-facing link for a quote on the tenant's own website
 * (https://<slug>.<tenant domain>/quote/<token>), falling back to the current
 * origin while the slug loads.
 */
export function useQuoteLinkBuilder(orgId: string | null | undefined) {
  const { data: slugInfo } = useOrgSlugInfo(orgId);
  const slug = slugInfo?.slug;
  return useCallback(
    (token: string) => `${slug ? buildWebsiteUrl(slug) : window.location.origin}${quotePath(token)}`,
    [slug],
  );
}
